import { Link } from '@inertiajs/react'
import { useEffect, type ReactNode } from 'react'
import PageHeader from '@/components/common/PageHeader'
import RemainingTimeDisplay from '@/components/reservations/RemainingTimeDisplay'

// STEP1（書類アップロード）廃止後は 問診→カード→受取 の3ステップ。
// currentStep キーは既存ページに合わせて interview2/3/4 のまま、表示ラベルのみ STEP1/2/3。
type Step = 'interview2' | 'interview3' | 'interview4'

type Props = {
  children: ReactNode
  currentStep: Step
  previousPageLink?: string
  title?: string
  expiresAt?: string
}

const steps: { key: Step; label: string }[] = [
  { key: 'interview2', label: 'STEP1' },
  { key: 'interview3', label: 'STEP2' },
  { key: 'interview4', label: 'STEP3' },
]

const clipPathForStep = (index: number, total: number) => {
  if (index === 0) {
    return 'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)'
  }

  if (index === total - 1) {
    return 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 14px 50%)'
  }

  return 'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)'
}

export default function ReservationLayout({
  children,
  currentStep,
  previousPageLink,
  title = 'オンライン診療予約',
  expiresAt,
}: Props) {
  const currentStepIndex = steps.findIndex((step) => step.key === currentStep)

  // ブラウザバックでbfcacheから復元された場合にリロードしてサーバーサイドチェックを実行
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload()
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  return (
    <>
      {expiresAt && <RemainingTimeDisplay expiresAt={expiresAt} />}
      {expiresAt && <div className="h-9" />}

      <div className="min-h-screen bg-white">
        <PageHeader
          leading={
            previousPageLink && (
              <Link
                href={previousPageLink}
                className="text-[#6f7680] hover:text-[#2a3039]"
                aria-label="前のページへ戻る"
              >
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
            )
          }
          subtitle={title}
        >
          <div className="mt-4 flex items-center">
            {steps.map((step, index) => {
              const stepClass =
                index === currentStepIndex
                  ? 'oc-step-item-active'
                  : index < currentStepIndex
                    ? 'oc-step-item-done'
                    : 'oc-step-item-todo'

              return (
                <div
                  key={step.key}
                  className={`oc-step-item ${stepClass} ${index > 0 ? '-ml-2' : ''}`}
                  style={{ clipPath: clipPathForStep(index, steps.length) }}
                >
                  {step.label}
                </div>
              )
            })}
          </div>
        </PageHeader>

        <main className="mx-auto max-w-3xl px-4 py-6">{children}</main>
      </div>
    </>
  )
}
